/**
 * EstimatorHub — Wave 30 Chunk 11f.
 *
 * Full-screen Estimator workflow. Reuses the same Hidro AI engine as the
 * Project Planner (lib/spec_engine.py) but is stand-alone: a rep can
 * walk into a customer site, voice-record a system description, and
 * walk out with a priced quote-ready estimate.
 *
 * Single-page UX (no modals, view + edit in the same surface):
 *   1. Header with "New estimate" button.
 *   2. Active drafts list — each row click expands into the full
 *      breakdown right below.
 *   3. Inline "draft a new estimate" panel when New is clicked.
 *   4. Open draft expands inline with sections, stock items (priced),
 *      non-stock provisions, gaps, recommendations, totals card, and
 *      a "Convert to quote" action.
 */
import { useEffect, useState, useMemo } from "react";
import {
  Calculator, Sparkles, ChevronDown, ChevronRight, Loader2, Lightbulb,
  Check, X, FileText, Trash2, Archive, RefreshCw, ReceiptText,
  AlertTriangle, Search,
} from "lucide-react";
import api from "../lib/api";
import VoiceTextarea from "../components/VoiceTextarea";
import { useBranding } from "../context/TenantBrandingContext";
import { toast } from "../components/ui/sonner";

const STATUS_PILL = {
  draft:      { c: "#647087", l: "Draft" },
  running:    { c: "#1FB6C1", l: "Mapping…" },
  ready:      { c: "#10B981", l: "Ready" },
  error:      { c: "#F43F5E", l: "Errored" },
  converted:  { c: "#A78BFA", l: "Converted" },
};

const fmtZAR = (n) => "R " + (Number(n) || 0).toLocaleString("en-ZA", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function EstimatorHub() {
  const { branding } = useBranding();
  const aiName = branding?.ai_name || "Hidro";
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [openDetail, setOpenDetail] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [search, setSearch] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/estimator/drafts", { params: { limit: 200 } });
      setDrafts(r.data?.items || []);
    } catch (err) {
      setDrafts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const loadOpen = async (did) => {
    try {
      const r = await api.get(`/estimator/drafts/${did}`);
      setOpenDetail(r.data);
    } catch (err) {
      setOpenDetail(null);
    }
  };

  useEffect(() => {
    if (openId) loadOpen(openId);
    else setOpenDetail(null);
  }, [openId]);

  // Poll the open draft + the list every 2s while ANY draft is running
  // so background AI completion is reflected without a manual refresh.
  useEffect(() => {
    const anyRunning =
      openDetail?.status === "running" ||
      (drafts || []).some((d) => d.status === "running");
    if (!anyRunning) return;
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      try {
        if (openId) {
          const r = await api.get(`/estimator/drafts/${openId}`);
          if (!cancelled) setOpenDetail(r.data);
        }
        const lr = await api.get("/estimator/drafts", { params: { limit: 200 } });
        if (!cancelled) setDrafts(lr.data?.items || []);
      } catch (_err) { /* keep polling */ }
    };
    const handle = setInterval(tick, 2000);
    return () => { cancelled = true; clearInterval(handle); };
  }, [openDetail?.status, drafts, openId]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return drafts;
    return drafts.filter((d) =>
      ((d.title || "") + " " + (d.description || "")).toLowerCase().includes(q),
    );
  }, [drafts, search]);

  return (
    <div className="px-4 sm:px-6 py-5 max-w-5xl mx-auto" data-testid="estimator-hub">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
        <div className="flex items-center gap-3 min-w-0">
          <Calculator className="w-6 h-6 text-[#1FB6C1] shrink-0" />
          <div className="min-w-0">
            <h1 className="font-display text-2xl font-bold tracking-tight">Estimator</h1>
            <p className="text-xs text-[#647087]">
              Voice-record a system on site → Hidro spec it → convert to a real quote.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} className="hv-btn hv-btn-ghost text-xs" data-testid="estimator-refresh">
            <RefreshCw className="w-3 h-3" /> Refresh
          </button>
          <button onClick={() => setShowNew((v) => !v)}
                  className={`hv-btn text-xs ${showNew ? "hv-btn-primary" : "hv-btn-primary"}`}
                  data-testid="estimator-new">
            <Sparkles className="w-3 h-3" /> {showNew ? "Cancel" : "New estimate"}
          </button>
        </div>
      </div>

      {showNew && (
        <NewEstimatePanel
          onCancel={() => setShowNew(false)}
          onCreated={(d) => { setShowNew(false); load(); setOpenId(d.id); }}
        />
      )}

      {/* Search */}
      <div className="relative mb-3">
        <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-[#647087]" />
        <input type="search" value={search} onChange={(e) => setSearch(e.target.value)}
               placeholder="Search drafts by title or description…"
               className="hv-input w-full pl-7 text-sm" data-testid="estimator-search" />
      </div>

      {loading && <div className="text-center py-12 text-[#647087]"><Loader2 className="w-4 h-4 animate-spin inline" /> Loading…</div>}

      {!loading && filtered.length === 0 && !showNew && (
        <div className="text-center py-12 text-[#647087]">
          <Calculator className="w-10 h-10 mx-auto mb-2 opacity-30" />
          <div className="text-sm">No estimator drafts yet.</div>
          <div className="text-[11px] mt-1">Tap <span className="text-[#1FB6C1]">New estimate</span> to record one.</div>
        </div>
      )}

      {!loading && filtered.length > 0 && (
        <div className="space-y-2" data-testid="estimator-drafts-list">
          {filtered.map((d) => (
            <DraftRow
              key={d.id}
              draft={d}
              isOpen={openId === d.id}
              onToggle={() => setOpenId((cur) => cur === d.id ? null : d.id)}
              detail={openId === d.id ? openDetail : null}
              onChanged={() => { load(); if (openId === d.id) loadOpen(d.id); }}
            />
          ))}
        </div>
      )}
    </div>
  );
}


function NewEstimatePanel({ onCancel, onCreated }) {
  const [description, setDescription] = useState("");
  const [title, setTitle] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [customerOptions, setCustomerOptions] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get("/customers", { params: { limit: 200 } });
        if (alive) setCustomerOptions(r.data?.items || r.data || []);
      } catch (err) {
        if (alive) setCustomerOptions([]);
      }
    })();
    return () => { alive = false; };
  }, []);

  const submit = async () => {
    if (!description.trim()) {
      toast.error("Describe the system first.");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/estimator/drafts", {
        description,
        title: title || null,
        customer_id: customerId || null,
      });
      const status = r.data?.status;
      if (status === "running") toast.message(`AI is mapping the system… results will appear in 30-120s.`);
      else if (status === "ready") toast.success("Estimate mapped.");
      else if (status === "error") toast.error("AI couldn't map — saved your text. Try recompute later.");
      else toast.success("Draft saved.");
      onCreated(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save draft.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="hv-card p-4 mb-3 border-[#1FB6C1]/30" data-testid="estimator-new-panel">
      <div className="text-[10px] uppercase tracking-wider text-[#1FB6C1] font-bold mb-2">
        New estimate · Hidro will spec the system from your description
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-2">
        <input
          className="hv-input w-full text-sm"
          placeholder="Title (optional)"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          data-testid="estimator-new-title"
        />
        <select
          className="hv-input w-full text-sm"
          value={customerId}
          onChange={(e) => setCustomerId(e.target.value)}
          data-testid="estimator-new-customer"
        >
          <option value="">Customer (optional)</option>
          {customerOptions.map((c) => (
            <option key={c.id} value={c.id}>{c.name || c.company_name || c.id.slice(0, 8)}</option>
          ))}
        </select>
      </div>
      <VoiceTextarea
        value={description}
        onChange={setDescription}
        onCommit={({ refined }) => setDescription(refined || "")}
        placeholder="e.g. 2000 LPH pre-treatment plus RO. Need FRP vessel for AFM and carbon with automatic flush. Bag filter 1 micron. 2000 LPH RO with 8 brackish membranes. Antiscalant dosing. R 80 000 fittings provision, R 30 000 electrical, 8 trips, 10 days labour…"
        rows={8}
        context="estimator"
        testIdPrefix="estimator-desc"
        className="hv-input w-full text-sm"
      />
      <div className="flex justify-end gap-2 mt-2">
        <button onClick={onCancel} className="hv-btn hv-btn-ghost text-xs">Cancel</button>
        <button onClick={submit} disabled={busy || !description.trim()}
                className="hv-btn hv-btn-primary text-xs disabled:opacity-50"
                data-testid="estimator-new-submit">
          {busy ? <><Loader2 className="w-3 h-3 animate-spin" /> Mapping…</> : <><Sparkles className="w-3 h-3" /> Generate estimate</>}
        </button>
      </div>
    </div>
  );
}


function DraftRow({ draft, isOpen, onToggle, detail, onChanged }) {
  const pill = STATUS_PILL[draft.status] || STATUS_PILL.draft;
  const grand = draft.totals?.grand_total ?? 0;
  return (
    <div className="hv-card overflow-hidden" data-testid={`estimator-draft-${draft.id}`}>
      <button
        type="button"
        onClick={onToggle}
        className="w-full text-left px-3 py-2.5 hover:bg-white/[0.02] flex items-center gap-2 flex-wrap"
      >
        <span className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded shrink-0"
              style={{ background: `${pill.c}1f`, color: pill.c }}>
          {pill.l}
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium text-white truncate">{draft.title}</div>
          <div className="text-[10px] text-[#647087]">{draft.created_at?.slice(0, 10)} · {draft.created_by_name || "—"}</div>
        </div>
        {grand > 0 && (
          <div className="text-sm font-mono text-[#10B981] shrink-0">{fmtZAR(grand)}</div>
        )}
        {isOpen ? <ChevronDown className="w-3 h-3 text-[#647087]" /> : <ChevronRight className="w-3 h-3 text-[#647087]" />}
      </button>
      {isOpen && (
        <div className="border-t border-white/5 p-3">
          {!detail && <div className="text-xs text-[#647087]"><Loader2 className="w-3 h-3 animate-spin inline" /> Loading…</div>}
          {detail && <DraftDetail draft={detail} onChanged={onChanged} />}
        </div>
      )}
    </div>
  );
}


function DraftDetail({ draft, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [recBusy, setRecBusy] = useState({});
  const totals = draft.totals || {};
  const recommendations = (draft.recommendations || []).filter((r) => !r.dismissed && !r.accepted);

  const recompute = async () => {
    setBusy(true);
    try {
      await api.post(`/estimator/drafts/${draft.id}/recompute`);
      toast.message("Recomputing in background… result will appear in 30-120s.");
      onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Recompute failed.");
    } finally { setBusy(false); }
  };

  const archive = async () => {
    if (!window.confirm("Archive this estimate?")) return;
    try {
      await api.delete(`/estimator/drafts/${draft.id}`);
      toast.success("Archived.");
      onChanged();
    } catch (err) {
      toast.error("Archive failed.");
    }
  };

  const convert = async () => {
    if (!draft.customer_id) {
      const cid = window.prompt("Customer ID to link this quote to (open the Customers page if you don't know it):");
      if (!cid) return;
      try {
        const r = await api.post(`/estimator/drafts/${draft.id}/convert-to-quote`, { customer_id: cid });
        toast.success(`Converted to quote ${r.data.quote_document_number}.`);
        onChanged();
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Convert failed.");
      }
      return;
    }
    if (!window.confirm("Create a quote in Sales Funnel from this estimate?")) return;
    try {
      const r = await api.post(`/estimator/drafts/${draft.id}/convert-to-quote`, {});
      toast.success(`Converted to quote ${r.data.quote_document_number}.`);
      onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Convert failed.");
    }
  };

  const actRec = async (rec, action) => {
    setRecBusy((b) => ({ ...b, [rec.id]: true }));
    try {
      await api.post(`/estimator/drafts/${draft.id}/recommendations/${rec.id}/${action}`);
      toast.success(action === "accept" ? "Added." : "Dismissed.");
      onChanged();
    } catch (err) {
      toast.error(`${action} failed.`);
    } finally {
      setRecBusy((b) => ({ ...b, [rec.id]: false }));
    }
  };

  return (
    <div className="space-y-3 text-xs">
      {/* Toolbar */}
      <div className="flex items-center gap-1.5 flex-wrap">
        <button onClick={recompute} disabled={busy} className="hv-btn hv-btn-ghost text-[11px]"
                data-testid="estimator-recompute">
          <RefreshCw className="w-3 h-3" /> Recompute
        </button>
        <button onClick={convert} disabled={busy || draft.status === "converted"}
                className="hv-btn hv-btn-primary text-[11px] disabled:opacity-50"
                data-testid="estimator-convert">
          <ReceiptText className="w-3 h-3" /> {draft.status === "converted" ? "Already converted" : "Convert to quote"}
        </button>
        <button onClick={archive} className="hv-btn hv-btn-ghost text-[11px] ml-auto"
                data-testid="estimator-archive">
          <Archive className="w-3 h-3" /> Archive
        </button>
      </div>

      {/* Summary */}
      {draft.system_summary && (
        <div className="bg-[#1FB6C1]/5 border border-[#1FB6C1]/20 rounded-md p-2.5">
          <div className="text-[10px] uppercase tracking-wider text-[#1FB6C1] font-bold mb-1">Summary</div>
          <div className="text-[#cdd5e3] leading-relaxed">{draft.system_summary}</div>
        </div>
      )}

      {/* Warnings */}
      {(draft.warnings || []).length > 0 && (
        <div className="bg-[#F59E0B]/10 border border-[#F59E0B]/30 rounded-md p-2.5">
          <div className="text-[10px] uppercase tracking-wider text-[#F59E0B] font-bold mb-1 flex items-center gap-1">
            <AlertTriangle className="w-3 h-3" /> Notes
          </div>
          <ul className="text-[#fcd9a1] list-disc ml-4 space-y-0.5">
            {draft.warnings.map((w, i) => <li key={i}>{w}</li>)}
          </ul>
        </div>
      )}

      {/* Recommendations */}
      {recommendations.length > 0 && (
        <div className="bg-[#1FB6C1]/8 border border-[#1FB6C1]/30 rounded-md p-2.5"
             data-testid="estimator-recommendations">
          <div className="text-[10px] uppercase tracking-wider text-[#1FB6C1] font-bold mb-2 flex items-center gap-1">
            <Lightbulb className="w-3 h-3" /> Did you forget?
          </div>
          <div className="space-y-1.5">
            {recommendations.map((r) => (
              <div key={r.id} className="flex items-start gap-2 bg-black/30 rounded p-2">
                <span className="text-[9px] uppercase font-mono px-1.5 py-0.5 rounded shrink-0 mt-0.5"
                      style={{
                        background: r.suggested_inventory_id ? "#10B98122" : "#A78BFA22",
                        color: r.suggested_inventory_id ? "#10B981" : "#A78BFA",
                      }}>
                  {r.suggested_inventory_id ? "STOCK" : (r.non_stock_kind || "REC")}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="text-white font-medium">
                    {r.suggested_name_resolved || r.suggested_name}
                    <span className="ml-1 text-[10px] text-[#647087]">· {r.qty} {r.unit}</span>
                  </div>
                  <div className="text-[10px] text-[#a0aabf] leading-snug mt-0.5">{r.reason}</div>
                </div>
                <button disabled={recBusy[r.id]} onClick={() => actRec(r, "accept")}
                        className="hv-btn hv-btn-primary text-[10px] px-2 py-1" title="Add">
                  <Check className="w-3 h-3" />
                </button>
                <button disabled={recBusy[r.id]} onClick={() => actRec(r, "dismiss")}
                        className="hv-btn hv-btn-ghost text-[10px] px-2 py-1" title="Dismiss">
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Items grouped by section */}
      <ItemsBySection
        stock={draft.stock_items || []}
        nonStock={draft.non_stock_items || []}
        gaps={draft.gaps || []}
      />

      {/* Totals card */}
      <div className="border border-white/5 bg-white/[0.02] rounded-md p-3">
        <div className="grid grid-cols-3 gap-2 text-[10px]">
          <Stat label="Stock" value={fmtZAR(totals.stock_total)} />
          <Stat label="Non-stock" value={fmtZAR(totals.non_stock_total)} />
          <Stat label="Grand total" value={fmtZAR(totals.grand_total)} highlight="#10B981" />
        </div>
      </div>

      {draft.converted_quote_id && (
        <div className="text-[10px] text-[#A78BFA]">
          ↳ Converted to quote <span className="font-mono">{draft.converted_quote_id.slice(0, 8)}</span> · find it in the Sales Funnel.
        </div>
      )}
    </div>
  );
}


function ItemsBySection({ stock, nonStock, gaps }) {
  const sections = new Set([
    ...stock.map((s) => s.section),
    ...nonStock.map((n) => n.section),
    ...gaps.map((g) => g.section),
  ]);
  const list = [...sections].filter(Boolean);
  if (list.length === 0) {
    return <div className="text-[#647087] text-[11px] italic">No items resolved yet.</div>;
  }
  return (
    <div className="space-y-2">
      {list.map((sec) => {
        const ss = stock.filter((s) => s.section === sec);
        const sn = nonStock.filter((s) => s.section === sec);
        const sg = gaps.filter((s) => s.section === sec);
        return (
          <div key={sec} className="border border-white/5 rounded">
            <div className="bg-white/[0.03] px-2 py-1.5 text-[10px] uppercase tracking-wider text-[#1FB6C1] font-bold">
              {sec}
            </div>
            <div className="divide-y divide-white/5">
              {ss.map((s) => <ItemLine key={s.id} item={s} bucket="stock" />)}
              {sn.map((s) => <ItemLine key={s.id} item={s} bucket="non_stock" />)}
              {sg.map((s) => <ItemLine key={s.id} item={s} bucket="gap" />)}
            </div>
          </div>
        );
      })}
    </div>
  );
}


function ItemLine({ item, bucket }) {
  const isDatasheet = bucket === "stock" && item.source === "datasheet";
  const badge = bucket === "stock"
    ? (isDatasheet
        ? { c: "#A78BFA", t: "DATA" }
        : { c: "#10B981", t: "STOCK" })
    : bucket === "non_stock"
      ? { c: "#A78BFA", t: (item.non_stock_kind || "NON-STOCK").toUpperCase() }
      : { c: "#F43F5E", t: "GAP" };
  const dsLabel = isDatasheet
    ? [item.manufacturer, item.part_number].filter(Boolean).join(" · ")
    : "";
  return (
    <div className="px-2 py-1.5 flex items-center gap-2 text-[11px]">
      <span className="text-[9px] font-mono uppercase px-1.5 py-0.5 rounded shrink-0"
            style={{ background: `${badge.c}1f`, color: badge.c }}
            title={isDatasheet ? "Matched from datasheet library" : undefined}>
        {badge.t}
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-white truncate">{item.name || item.description}</div>
        <div className="text-[9px] text-[#647087] truncate">
          {isDatasheet && dsLabel && <span className="text-[#A78BFA] font-mono">{dsLabel} · </span>}
          {!isDatasheet && item.sku && <span className="font-mono">{item.sku} · </span>}
          {item.role || item.description || ""}
        </div>
        {isDatasheet && item.datasheet_url && (
          <a
            href={item.datasheet_url}
            target="_blank"
            rel="noreferrer"
            className="text-[9px] text-[#A78BFA] hover:underline mt-0.5 inline-block"
            data-testid={`estimator-datasheet-link-${item.datasheet_id}`}
          >
            Open datasheet ↗
          </a>
        )}
      </div>
      <div className="text-right shrink-0">
        <div className="font-mono text-[#a0aabf]">{item.qty} {item.unit || "pcs"}</div>
        {item.line_total != null && (
          <div className="font-mono text-[#10B981] text-[10px]">{fmtZAR(item.line_total)}</div>
        )}
      </div>
    </div>
  );
}


function Stat({ label, value, highlight }) {
  return (
    <div className="rounded p-2 bg-white/[0.02] border border-white/5">
      <div className="uppercase tracking-wider text-[#647087]">{label}</div>
      <div className="font-mono text-sm font-bold" style={{ color: highlight || "#cdd5e3" }}>
        {value}
      </div>
    </div>
  );
}
